<script setup lang="ts">
/**
 * The one dialog that stands between a click and the end of the session.
 *
 * Rendered once near the root in `App.vue` and driven entirely by
 * `state/confirm-sign-out` — it has no props and knows nothing about the garage
 * or the verify gate, which is what lets all three Sign out buttons share it.
 * The shape is `ui/ConfirmPurchase.vue`'s on purpose: two dialogs that ask a
 * yes/no question should not be two designs.
 *
 * Three things about it are load-bearing.
 *
 * **Escape and the backdrop both decline**, and the keydown is stopped rather
 * than allowed to bubble. That is not tidiness here — the compact Sign out is
 * inside `SettingsPanel`'s `actions` slot, and the garage installs a
 * *window-level* Escape listener that toggles that window (see
 * `SettingsPanel.vue`, *The Escape key is the host's too*). Without
 * `stopPropagation` one keypress would decline this dialog **and** shut the
 * settings window under it, so cancelling would cost the player the panel they
 * were in.
 *
 * **Focus moves to Cancel, and the confirm button is not autofocused.** A
 * Return key on an autofocused confirm is exactly the accident this exists to
 * prevent. Same discipline as the purchase dialog, and it is also what puts the
 * focus *inside* the dialog so the keydown above has somewhere to start.
 *
 * **The confirm is red rather than the accent orange.** The purchase dialog's
 * commit is the accent because a spend is a *reward*; this is the app's one
 * destructive control, and `#d2544a` is the red it already wears — on the title
 * bar's `.embossed.danger` hover, on `.bay-remove`, and on
 * `NotificationBanner`'s error. Highlighted, as asked for, without borrowing the
 * colour that means "you got something".
 */
import { nextTick, ref, watch } from 'vue';

import { cancelSignOut, signOutAsked } from '@/state/confirm-sign-out';
import { useSession } from '@/state/session';

const session = useSession();

const cancelButton = ref<HTMLButtonElement | null>(null);

/**
 * True from the moment Sign out is pressed until the session is gone.
 *
 * The request is a round trip, and a second press would send a second one. It
 * also stops the dialog reading as unresponsive on a slow connection: the
 * screen behind it does not change until `player` is null.
 */
const going = ref(false);

watch(signOutAsked, async (open) => {
  if (!open) return;
  going.value = false;
  await nextTick();
  cancelButton.value?.focus();
});

function onKeydown(event: KeyboardEvent): void {
  if (event.key !== 'Escape') return;
  // See the note above: the garage's own window-level listener must not see this.
  event.stopPropagation();
  if (!going.value) cancelSignOut();
}

async function confirm(): Promise<void> {
  if (going.value) return;
  going.value = true;
  await session.logout();
  // Closed after the session is gone, so the dialog is never left standing over
  // a screen that has already swapped underneath it.
  cancelSignOut();
}
</script>

<template>
  <div
    v-if="signOutAsked"
    class="scrim"
    data-test="confirm-sign-out"
    @click.self="going || cancelSignOut()"
    @keydown="onKeydown"
  >
    <div class="dialog" role="alertdialog" aria-modal="true" aria-labelledby="sign-out-title">
      <h2 id="sign-out-title">Sign out</h2>

      <p class="says">
        You are about to sign out and the session will end here. Your garage,
        your bays and your progress stay on the account — sign in again to pick
        them up.
      </p>

      <div class="actions">
        <button
          ref="cancelButton"
          type="button"
          class="ghost"
          data-test="sign-out-cancel"
          :disabled="going"
          @click="cancelSignOut()"
        >
          Cancel
        </button>
        <button
          type="button"
          class="accept"
          data-test="sign-out-confirm"
          :disabled="going"
          @click="confirm()"
        >
          {{ going ? 'Signing out…' : 'Sign out' }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
/*
 * 100, `ConfirmPurchase`'s own figure, and the two can never be up together —
 * every Sign out control is behind the purchase dialog's scrim while it is
 * open. Above `SettingsPanel`'s 60, which is the surface the compact Sign out
 * is *inside*; below `RotatePrompt`'s 101, which is a question about the device
 * and comes first.
 */
.scrim {
  position: fixed;
  inset: 0;
  z-index: 100;
  display: grid;
  place-items: center;
  padding: 1rem;
  background: rgba(4, 6, 9, 0.68);
  backdrop-filter: blur(3px);
}

/*
 * The dialog never scrolls and its buttons are never below the fold — the one
 * failure a confirmation can least afford, and the reason `ConfirmPurchase`
 * carries the same pair. Four lines of copy at 21rem fits a 375px-tall phone
 * with room to spare, so this is the guard rather than the mechanism: it is what
 * stops a future third sentence pushing Cancel off the bottom instead of
 * shortening the box.
 */
.dialog {
  display: flex;
  flex-direction: column;
  width: min(21rem, 100%);
  max-height: calc(100dvh - 2rem);
  overflow: hidden;
  padding: 1.1rem 1.2rem 1rem;
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 5px;
  background: var(--steel-800);
  box-shadow: 0 1.2rem 3rem rgba(0, 0, 0, 0.6);
  text-align: left;
}

h2 {
  flex: none;
  margin: 0 0 0.6rem;
  color: var(--steel-400);
  font-size: 0.66rem;
  font-weight: 600;
  letter-spacing: 0.18em;
  text-transform: uppercase;
}

/* The one child allowed to give way, so the heading and both buttons stay put. */
.says {
  flex: 0 1 auto;
  min-height: 0;
  overflow-y: auto;
  margin: 0;
  color: var(--steel-200);
  font-size: 0.85rem;
  line-height: 1.5;
}

.actions {
  flex: none;
  display: flex;
  justify-content: flex-end;
  gap: 0.6rem;
  margin-top: 1.1rem;
}

.ghost {
  padding: 0.5rem 0.9rem;
  border: 1px solid var(--steel-600);
  border-radius: 3px;
  background: transparent;
  color: var(--steel-200);
  font: inherit;
  font-size: 0.82rem;
  cursor: pointer;
  transition:
    border-color 120ms ease-out,
    color 120ms ease-out,
    background 120ms ease-out;
}

.ghost:hover:not(:disabled) {
  border-color: var(--steel-400, #9aa5b1);
  color: #fff;
}

.ghost:active:not(:disabled) {
  background: rgba(255, 255, 255, 0.05);
}

.ghost:focus-visible {
  outline: 1px solid var(--steel-400, #9aa5b1);
  outline-offset: 1px;
}

/* The commit: filled, and the only button here that lifts. See the header for
   why it is red and not the accent. */
.accept {
  padding: 0.5rem 1.3rem;
  border: 1px solid #d2544a;
  border-radius: 3px;
  background: #d2544a;
  color: #fff;
  font: inherit;
  font-size: 0.82rem;
  font-weight: 700;
  cursor: pointer;
  transition:
    transform 120ms ease-out,
    box-shadow 120ms ease-out,
    background 120ms ease-out;
}

.accept:hover:not(:disabled) {
  background: color-mix(in srgb, #d2544a 88%, white 12%);
  box-shadow: 0 0.3rem 0.7rem rgba(0, 0, 0, 0.35);
  transform: translateY(-1px);
}

.accept:active:not(:disabled) {
  background: color-mix(in srgb, #d2544a 85%, black 15%);
  box-shadow: none;
  transform: translateY(0);
}

.accept:focus-visible {
  outline: 1px solid #fff;
  outline-offset: 1px;
}

.ghost:disabled,
.accept:disabled {
  opacity: 0.45;
  cursor: default;
}

/* The 44px tap floor, asked of the device — the same question the settings
   window asks in its own stylesheet. */
@media (hover: none) and (pointer: coarse) {
  .ghost,
  .accept {
    min-height: 44px;
  }
}
</style>
